import type { IRegisterForm, IRegisterResponse } from '@/types/IRegister';
import type { IAPIRes } from '@/types/IAPIRes';
import type { ILoginForm, ILoginResponse } from '@/types/ILogin';
import type { IForgetPasswordFrom } from '@/types/IForgetPasswordFrom';
import { useAxios} from '@/boot/axios';

export interface IAuthService {
  register: (
    userData: IRegisterForm,
  ) => Promise<IAPIRes<{ otp_count: number }>>;
  login: (userData: ILoginForm) => Promise<IAPIRes<ILoginResponse>>;
  logout: () => Promise<IAPIRes<null>>;
}

export const register = async (
  userData: IRegisterForm,
  token: string
): Promise<IAPIRes<{ otp_count: number, message?: string }>> => {
  const { axiosInstance } = useAxios();
  const formData = new FormData();
  Object.entries(userData).forEach(([key, value]) => {
    formData.append(key, value.trim());
  });
  formData.append('token', token);
  formData.append('action', 'register');
  const response = await axiosInstance.post<IAPIRes<{ otp_count: number, message?: string }>>(
    '/register',
    formData,
  );
  return response.data;
};

export const login = async (
  userData: ILoginForm,
  token: string
): Promise<IAPIRes<ILoginResponse>> => {
  const { axiosInstance } = useAxios();
  const formData = new FormData();

  Object.entries(userData).forEach(([key, value]) => {
    if (key === 'remember') {
      formData.append(key, value === true ? '1' : '0');
    } else {
      formData.append(key, value);
    }
  });
  formData.append('token', token);
  formData.append('action', 'login');
  const response = await axiosInstance.post<IAPIRes<ILoginResponse>>(
    '/login',
    formData,
  );
  return response.data;
};

export const sendOTP = async (
  email: string,
  token: string,
): Promise<IAPIRes<{msg:string,otp_count: number, message?: string}>> => {
  const { axiosInstance } = useAxios();
  const response = await axiosInstance.post<
    IAPIRes<{ msg: string; otp_count: number; message?: string }>
  >('/send-otp', { email, token });
  return response.data;
};

export const submitOTP = async (
  email: string,
  otp: number,
): Promise<IAPIRes<IRegisterResponse>> => {
  const { axiosInstance } = useAxios();
  const response = await axiosInstance.post<IAPIRes<IRegisterResponse>>(
    '/check-otp-phone',
    { email, otp },
  );
  return response.data;
};

export const logout = async (): Promise<IAPIRes<{ data: string }>> => {
  const { axiosAuth } = useAxios();
  const response = await axiosAuth.get<IAPIRes<{ data: string }>>('/logout');
  return response.data;
};

export const forgotPassword = async (
  email: string,
): Promise<IAPIRes<{ data: string }>> => {
  const { axiosInstance } = useAxios();
  const formData = new FormData();
  formData.append('email', email);

  const response = await axiosInstance.post<IAPIRes<{ data: string }>>(
    '/forgot-password',
    formData,
  );
  return response.data;
};

export const checkOTP = async (
  otp: string,
  email: string,
): Promise<IAPIRes<{ data: string }>> => {
  const { axiosInstance } = useAxios();
  const formData = new FormData();
  formData.append('otp', otp);
  formData.append('email', email);

  const response = await axiosInstance.post<IAPIRes<{ data: string }>>(
    '/check-otp',
    formData,
  );
  return response.data;
};

export const resetPasswordAPI = async (
  data: IForgetPasswordFrom,
): Promise<IAPIRes<any>> => {
  const { axiosInstance } = useAxios();
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    formData.append(key, value);
  });

  const response = await axiosInstance.post<IAPIRes<any>>(
    '/reset-password',
    formData,
  );
  return response.data;
};

export const requestAccountRestoration = async (
  email: string,
): Promise<IAPIRes<{ data: string }>> => {
  const { axiosInstance } = useAxios();
  const formData = new FormData();
  formData.append('email', email);

  const response = await axiosInstance.post<IAPIRes<{ data: string }>>(
    '/request-account-restoration',
    formData,
  );
  return response.data;
};

export const verifyRestorationOtp = async (
  email: string,
  otp: string
): Promise<IAPIRes<{ data: string }>> => {
  const { axiosInstance } = useAxios();
  const formData = new FormData();
  formData.append('email', email);
  formData.append('otp', otp);

  const response = await axiosInstance.post<IAPIRes<{ data: string }>>(
    '/verify-restoration-otp',
    formData,
  );
  return response.data;
};
